import React, { useEffect, useState } from "react";
import cx from "classnames";
import { isEqual } from "lodash";

import { useOutsideClickHook } from "utils/outsideClick";

import InfoTooltip from "components/InfoTooltip";
import LoadingDots from "components/LoadingDots";
import WarningTooltip from "components/WarningTooltip";
import Checkbox from "components/Input/Checkbox";

import classes from "./style.module.scss";
import sharedClasses from "../style.module.scss";

import ArrowDown from "assets/images/icons/JSX/arrow-down";

export const DropDown = ({
    className,
    options,
    selected = [],
    onChange,
    open,
    footer: Footer,
    dropDownWidth,
    emptyMessage = "No options meet the requirements.",
    hasSelectAll = false,
    selectAllName,
    dropdownStyles = {},
    idPrefix = "",
    isProcessing = false,
}) => {
    const classNames = [classes.dropDown, open && classes.open, className && className].filter((clss) => clss);

    const allAvailableOptions = options.filter(({ disabled = false }) => !disabled).map(({ value }) => value);
    const allSelected =
        allAvailableOptions.length > 0 && allAvailableOptions.every((option) => selected.includes(option));

    const nameFor = (value) => (idPrefix ? `${idPrefix}-${value}` : `${value}`);

    return (
        <div className={classNames.join(" ")} style={{ width: dropDownWidth, ...dropdownStyles }}>
            {options.length > 0 ? (
                <>
                    <div className={cx(classes.options, isProcessing && classes.optionsObscured)}>
                        {options.map(({ value, label, disabled = false, image }, index) => {
                            const classNames = [classes.option, isEqual(selected, value) && classes.selected].filter(
                                (c) => c
                            );

                            return (
                                <Checkbox
                                    className={classNames}
                                    input={{
                                        value: selected.includes(value), // checked
                                        onChange: (event) => {
                                            if (disabled || isProcessing) {
                                                return;
                                            }

                                            let newValue = selected;

                                            if (!selected.includes(value)) {
                                                newValue = [...selected, value];
                                            } else {
                                                newValue = [...selected].filter((item) => item !== value);
                                            }

                                            onChange(newValue);
                                        },
                                    }}
                                    label={label}
                                    name={nameFor(value)}
                                    disabled={disabled || isProcessing}
                                    image={image}
                                    key={index}
                                />
                            );
                        })}
                    </div>
                    {hasSelectAll && (
                        <div className={classes.selectAll}>
                            {isProcessing ? (
                                <div className={classes.selectAllLoading}>
                                    <LoadingDots />
                                </div>
                            ) : (
                                <Checkbox
                                    className={classes.selectAllCheckbox}
                                    input={{
                                        value: allSelected, // checked
                                        onChange: (event) => {
                                            let newValue = selected;

                                            if (allSelected) {
                                                newValue = [];
                                            } else {
                                                newValue = allAvailableOptions;
                                            }

                                            onChange(newValue, { fromSelectAll: true });
                                        },
                                    }}
                                    label={"Select All"}
                                    name={idPrefix ? `${idPrefix}-selectAll` : selectAllName || "selectAll"}
                                    disabled={allAvailableOptions.length === 0}
                                />
                            )}
                        </div>
                    )}
                </>
            ) : (
                <div className={classes.emptyFilter}>
                    <span>{emptyMessage}</span>
                </div>
            )}
            {Footer && <Footer />}
        </div>
    );
};

const Select = ({
    name,
    label,
    input,
    className,
    large,
    hideLabel = false,
    options = [],
    disabled = false,
    placeholder = "Select",
    updateSelected,
    info = "",
    meta = {},
    forcedLabel = "",
    parentError = false,
    search = false,
    searchPlaceholder = "",
    footer = null,
    dropDownWidth = "100%",
    isLoading = false,
    warning = "",
    hasSelectAll = false,
    dropdownStyles = {},
    customStyles = {},
    customLabel,
    selectAllName,
    placeholderClassName = "",
    dropdownClassName = "",
    labelWithImages = false,
    images = [],
    onBlurAction = () => {},
    idPrefix = "",
    emptyMessage = "No options meet the requirements.",
    isNewDesign = false,
    labelIcon = null,
    isProcessing = false,
}) => {
    const { invalid = false, error = "", touched = false } = meta;

    const classNames = [
        classes.selectField,
        sharedClasses.inputField,
        className && className,
        large && sharedClasses.large,
        disabled && classes.disabled,
        hideLabel && classes.labelHidden,
        (parentError || invalid) && touched && sharedClasses.invalid,
        warning && classes.warning,
    ].filter((c) => c);

    const { value = [], onChange } = input;
    const [open, toggleOpen] = useState(false);
    //const [selected, toggleSelected] = useState('');
    const ref = useOutsideClickHook(() => toggleOpen(false));

    const setLabel = forcedLabel ? forcedLabel : `${value.length} Selected`;
    const hasSelection = value.length > 0;
    const newDesignLabelFloating = open || hasSelection;

    const handleChange = (newValue, meta) => {
        if (updateSelected) {
            updateSelected(newValue);
        }
        onChange(newValue, meta);
    };

    useEffect(() => {
        if (!open) {
            onBlurAction();
        }
    }, [open]);

    const selectValue = (
        <div
            className={`${sharedClasses.selectInput} ${classes.value} ${open && classes.open} ${
                disabled && `${classes.disabled} ${sharedClasses.disabled}`
            }`}
            onClick={() => !disabled && toggleOpen(!open)}
        >
            {labelIcon && <img src={labelIcon} alt="multi-select-icon" />}
            {customLabel ? (
                customLabel
            ) : hasSelection ? (
                <span className={classes.selected}>{setLabel}</span>
            ) : isNewDesign ? (
                <span className={`${classes.placeholder} ${placeholderClassName}`}>&nbsp;</span>
            ) : (
                <span className={`${classes.placeholder} ${placeholderClassName}`}>
                    {hideLabel ? setLabel : placeholder}
                </span>
            )}
            {labelWithImages && images.length > 0 && (
                <div className={classes.imageContainer}>
                    {options.map(({ value: optionValue, image }, index) => (
                        <img
                            key={index}
                            src={image}
                            alt=""
                            style={{ opacity: value.includes(optionValue) ? 1 : 0.5 }}
                            className={classes.image}
                        />
                    ))}
                </div>
            )}
            <ArrowDown />
        </div>
    );

    return (
        <div ref={ref} className={classNames.join(" ")} style={customStyles}>
            {label && (
                <label className={hideLabel ? "invisible" : ""} htmlFor={name}>
                    {label}
                </label>
            )}
            {isLoading && (
                <div className={classes.loading}>
                    <LoadingDots />
                </div>
            )}
            {info && <InfoTooltip className={sharedClasses.infoTooltip} info={info} />}
            {warning && (
                <WarningTooltip className={`${classes.warningTooltip} ${info && classes.pushLeft}`} warning={warning} />
            )}
            {!isLoading &&
                (isNewDesign ? (
                    <div className={sharedClasses.newDesignInputWrap}>
                        <span
                            className={cx(sharedClasses.newDesignLabel, {
                                [sharedClasses.activeNewDesignLabel]: newDesignLabelFloating,
                            })}
                        >
                            {label}
                        </span>
                        {selectValue}
                    </div>
                ) : (
                    selectValue
                ))}
            {!isLoading && (
                <DropDown
                    className={`${classes.dropDown} ${dropdownClassName}`}
                    options={options}
                    selected={value}
                    onChange={handleChange}
                    open={open}
                    toggleOpen={toggleOpen}
                    search={search}
                    searchPlaceholder={searchPlaceholder}
                    footer={footer}
                    dropDownWidth={dropDownWidth}
                    hasSelectAll={hasSelectAll}
                    selectAllName={selectAllName}
                    dropdownStyles={dropdownStyles}
                    idPrefix={idPrefix}
                    emptyMessage={emptyMessage}
                    isProcessing={isProcessing}
                />
            )}
            {invalid && error && touched && typeof error === "string" && (
                <span style={{ bottom: "-1.25rem" }} className={sharedClasses.errorMessage}>
                    {error}
                </span>
            )}
        </div>
    );
};

export default Select;
